import type { AppConfig } from "@/registry/appRegistry";
import { REMOTE_MAP } from "@/registry/remoteMap";
import { ComponentType, lazy, Suspense } from "react";
import ErrorBoundary from "./ErrorBoundary";

function UnavailableRemote() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center">
        <h2 className="text-xl font-bold text-gray-500 mb-2">Coming Soon</h2>
        <p className="text-gray-400">This application is not yet available.</p>
      </div>
    </div>
  );
}

function safeLazy(importFn: () => Promise<{ default: ComponentType }>) {
  return lazy(() =>
    importFn().catch(() => ({ default: UnavailableRemote }))
  );
}

// Build lazy components from the shared REMOTE_MAP
const remoteComponents: Record<string, React.LazyExoticComponent<ComponentType>> =
  Object.fromEntries(
    Object.entries(REMOTE_MAP).map(([id, imports]) => [id, safeLazy(imports.App)])
  );

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-navy mx-auto mb-4" />
        <p className="text-gray-500 dark:text-gray-400">Loading application...</p>
      </div>
    </div>
  );
}

interface RemoteAppProps {
  appConfig: AppConfig;
}

export default function RemoteApp({ appConfig }: RemoteAppProps) {
  const RemoteComponent = remoteComponents[appConfig.id];

  if (!RemoteComponent) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <h2 className="text-xl font-bold text-red-600 mb-2">Application Not Found</h2>
          <p className="text-gray-600 dark:text-gray-400">
            No remote module registered for "{appConfig.id}".
          </p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary appName={appConfig.name}>
      <Suspense fallback={<LoadingFallback />}>
        <RemoteComponent />
      </Suspense>
    </ErrorBoundary>
  );
}
